import { Table } from "@4uhub/lib4uhub";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import MainApi from "../../../../services/mainApi.service";
import MacroFilter, { IFilterParams } from "./MacroFilter";
import EsignEnvelopeEmailSendModal from "./ForubusSignatureModal/EsignEnvelopeEmailSendModal";
import ForubusSignatureDetailsModal from "./ForubusSignatureModal/ForubusSignatureDetailsModal";
import { IForubusSignatureLogData } from "../../../../models/forubus-signature-log";

const FORUBUS_SIGNATURE_ROUTE =
  process.env.REACT_APP_EXTERNAL + "/api/v1/ForubusSignature/ForubusSignatures";

const nfeTotvsLogService = new MainApi<IForubusSignatureLogData>(
  FORUBUS_SIGNATURE_ROUTE,
);

const translationPath = "4uESign.page.logs.forubusSignatureLogs.";

const ForubusSignature = () => {
  const [refresh, setRefresh] = useState(0);
  const today = useMemo(() => new Date(), []);

  const [filterParams, setFilterParams] = useState<IFilterParams>({
    FirstIntegrationId: "",
    CreatedStart: today,
    CreatedEnd: today,
  });

  const [selectedRow, setSelectedRow] =
    useState<IForubusSignatureLogData | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [emailSendRow, setEmailSendRow] =
    useState<IForubusSignatureLogData | null>(null);

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const columns: GridColDef<IForubusSignatureLogData>[] = [
    {
      field: "signatureType",
      headerName: t(translationPath + "signature_type") || "Tipo de Assinatura",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "firstIntegrationId",
      headerName:
        t(translationPath + "first_integration_id") || "Identificador",
      description:
        t(translationPath + "first_integration_id_hint") ||
        "Pode ser o número da receita, do atestado ou do atendimento",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "documentCode",
      headerName: t(translationPath + "document_code") || "Código do Documento",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "gedIntegrationId",
      headerName: t(translationPath + "ged_integration_id") || "Integração GED",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "finishDate",
      headerName: t(translationPath + "finish_date") || "Data de Conclusão",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";

        const dateStr = params.value.toString();
        if (
          dateStr.startsWith("0001-01-01") ||
          dateStr === "0001-01-01T00:00:00"
        ) {
          return "-";
        }

        const date = new Date(params.value);
        if (isNaN(date.getTime()) || date.getFullYear() === 1) {
          return "-";
        }

        return format(date, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
  ];

  const handleFilterChange = useCallback(
    (newFilters: IFilterParams | ((prev: IFilterParams) => IFilterParams)) => {
      setFilterParams(newFilters);
      setRefresh((prev) => prev + 1);
    },
    [],
  );

  const handleOpenModal = useCallback((row: IForubusSignatureLogData) => {
    setSelectedRow(row);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedRow(null);
  }, []);

  const handleOpenEmailSendModal = useCallback(
    (row: IForubusSignatureLogData) => {
      setEmailSendRow(row);
    },
    [],
  );

  const handleCloseEmailSendModal = useCallback(() => {
    setEmailSendRow(null);
  }, []);

  return (
    <>
      {modalOpen && selectedRow && (
        <ForubusSignatureDetailsModal
          data={selectedRow}
          onClose={handleCloseModal}
        />
      )}

      {emailSendRow && (
        <EsignEnvelopeEmailSendModal
          data={emailSendRow}
          onClose={handleCloseEmailSendModal}
        />
      )}

      <Table<IForubusSignatureLogData>
        title={t("components.menu.4uESign.logs.forubusSignatureLogs")}
        columns={columns}
        service={nfeTotvsLogService}
        searchInputPlaceHolder={
          t(translationPath + "search_placeholder") || "Search..."
        }
        showDefaultMacroFilters={false}
        showButton={false}
        showDeleteButton={false}
        defaultActions={true}
        getRowId={(row) => row.identifier}
        rowActions={(params) => [
          {
            action: () => handleOpenModal(params.row),
            icon: <VisibilityIcon fontSize="small" />,
            text: t(translationPath + "view_details") || "Ver detalhes",
          },
          {
            action: () => handleOpenEmailSendModal(params.row),
            icon: <EmailOutlinedIcon fontSize="small" />,
            text: t(translationPath + "emailSend.action"),
          },
        ]}
        serviceProps={useMemo(
          () => ({
            refresh,
            ...filterParams,
            CreatedStart: filterParams?.CreatedStart
              ? new Date(filterParams.CreatedStart.setHours(15))
                  .toISOString()
                  .substring(0, 10)
              : undefined,
            CreatedEnd: filterParams?.CreatedEnd
              ? new Date(filterParams.CreatedEnd.setHours(15))
                  .toISOString()
                  .substring(0, 10)
              : undefined,
          }),
          [refresh, filterParams],
        )}
        macroFilters={useCallback(
          () => (
            <MacroFilter
              filterParams={filterParams}
              onFilterParams={handleFilterChange}
            />
          ),
          [filterParams, handleFilterChange],
        )}
      />
    </>
  );
};

export default ForubusSignature;
